import { useState, useEffect, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/config/api";

export const ProtectedRoute = ({
  children,
  requireSubscription = true,
  requireRole = null,
}) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [pendingSubscriptionId, setPendingSubscriptionId] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  const checkSubscription = useCallback(async () => {
    const isSuperAdmin = user?.is_super_admin === true;
    if (isSuperAdmin) {
      setSubscriptionStatus("active");
      setCheckingSubscription(false);
      return;
    }

    try {
      await api.subscriptions.getCurrent();
      setSubscriptionStatus("active");
      setPendingSubscriptionId(null);
    } catch (error) {
      // 404 or "No active subscription found" means no subscription - this is expected
      if (
        error.message?.includes("No active subscription found") ||
        error.message?.includes("404") ||
        error.message?.includes("Not found") ||
        error.message?.includes("subscription") ||
        error.message?.includes("Active subscription required")
      ) {
        // If there's a pending subscription, send the user to payment
        try {
          const mySubs = await api.subscriptions.getMySubscriptions();
          const pending = (mySubs || []).find(
            (s) =>
              (s?.status === "pending" || s?.payment_status === "pending") &&
              s?.id,
          );
          const userRole = (
            user?.role != null ? String(user.role) : ""
          ).toLowerCase();
          const isTeamMember = ["sales", "finance", "management"].includes(
            userRole,
          );
          if (isTeamMember) {
            // Team users should never be routed to subscribe/payment.
            // They depend on company admin subscription ownership.
            setPendingSubscriptionId(null);
            setSubscriptionStatus("company_inactive");
          } else if (pending?.id) {
            setPendingSubscriptionId(pending.id);
            setSubscriptionStatus("pending");
          } else {
            setPendingSubscriptionId(null);
            setSubscriptionStatus("inactive");
          }
        } catch (_) {
          const userRole = (
            user?.role != null ? String(user.role) : ""
          ).toLowerCase();
          const isTeamMember = ["sales", "finance", "management"].includes(
            userRole,
          );
          setPendingSubscriptionId(null);
          setSubscriptionStatus(isTeamMember ? "company_inactive" : "inactive");
        }
      } else {
        // For payment page, allow access even without active subscription
        if (location.pathname.includes("/payment")) {
          setPendingSubscriptionId(null);
          setSubscriptionStatus("pending");
        } else {
          setPendingSubscriptionId(null);
          setSubscriptionStatus("inactive");
        }
      }
    } finally {
      setCheckingSubscription(false);
    }
  }, [location.pathname, user?.role, user?.is_super_admin]);

  useEffect(() => {
    if (isAuthenticated && requireSubscription) {
      checkSubscription();
    } else {
      setCheckingSubscription(false);
    }
  }, [checkSubscription, isAuthenticated, requireSubscription]);

  if (loading || checkingSubscription) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Allow payment page even without active subscription
  if (location.pathname.includes("/payment")) {
    return children;
  }

  // Check subscription for other protected routes
  if (requireSubscription && subscriptionStatus === "company_inactive") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-stone-50">
        <div className="max-w-md w-full bg-white border border-stone-200 rounded-xl shadow-sm p-6 text-center">
          <h2 className="text-lg font-semibold text-stone-800 mb-2">
            Subscription Required
          </h2>
          <p className="text-sm text-stone-600">
            Your company subscription expired. Contact your admin to renew it.
          </p>
        </div>
      </div>
    );
  }

  if (requireSubscription && subscriptionStatus === "pending") {
    if (pendingSubscriptionId) {
      return <Navigate to={`/payment/${pendingSubscriptionId}`} replace />;
    }
    return <Navigate to="/subscribe" replace />;
  }

  if (requireSubscription && subscriptionStatus === "inactive") {
    return <Navigate to="/subscribe" replace />;
  }

  // Check role requirement (supports single role string or comma-separated roles; case-insensitive)
  if (requireRole) {
    const allowedRoles = requireRole
      .split(",")
      .map((r) => r.trim().toLowerCase());
    const userRole = (
      user?.role != null ? String(user.role) : ""
    ).toLowerCase();
    if (!allowedRoles.includes(userRole)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Super admin only: redirect non–super-admin to dashboard
  if (location.pathname === "/super-admin" && !user?.is_super_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};
